import type { CSSProperties } from "react"
import * as RadioGroup from "@radix-ui/react-radio-group"
import * as Switch from "@radix-ui/react-switch"
import * as Tooltip from "@radix-ui/react-tooltip"


interface FICPartyInvolvedCardProps {
  partyName: string
  partyType?: string // can be ENUM later. Optional Party Type can be a valid (however outlier) business case, hence supported in design
  fraudStatus?: "fraud" | "not-fraud"
  generateRO?: boolean
  onFraudStatusChange?: (value: "fraud" | "not-fraud") => void
  onGenerateROChange?: (checked: boolean) => void
  generateRODisabled?: boolean;
}

const styles: { [key: string]: CSSProperties } = {
  outerCard: {
    display: "flex",
    padding: "12px",
    justifyContent: "space-between", // Keep this
    alignItems: "center",
    alignSelf: "stretch",
    borderRadius: "14px",
    border: "1px solid #E5E5E5",
    backgroundColor: "#FFFFFF",
    height: "72px",
    // minHeight: "50px",
    minWidth: "600px", // Ensure minimum card width
  },
  partySection: {
    display: "flex",
    padding: "12px",
    flexDirection: "column",
    gap: "4px",
    flex: "1 1 200px", // Grow, shrink, min basis 200px
    maxWidth: "350px", // Prevent it from growing too much
    minWidth: "200px", // Ensure minimum width for text
  },
  partyName: {
    color: "#070B24",
    fontFamily: "Inter, sans-serif",
    fontStyle: "normal",
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "20px",
    margin: 0,
    // Handle text overflow gracefully
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  partyBadge: {
    display: "inline-flex",
    padding: "2px 8px",
    borderRadius: "8px",
    backgroundColor: "#D1D5DB",
    color: "#4B5563",
    fontSize: "12px",
    fontWeight: 600,
    width: "fit-content",
    // Handle badge overflow
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "150px",
  },
  controlsSection: {
    display: "flex",
    alignItems: "center",
    gap: "40px",
    flexShrink: 0, // Prevent shrinking
    minWidth: "360px", // Ensure enough space for all controls
  },
  radioGroup: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    flexShrink: 0, // Don't shrink radio buttons
  },
  radioButton: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  radioButtonContainer: {
    display: "flex",
    width: "120px",
    padding: "12px",
    alignItems: "center",
    gap: "12px",
    borderRadius: "10px",
    border: "1px solid #E5E5E5",
    backgroundColor: "#FFFFFF",
    cursor: "pointer",
    justifyContent: "flex-start",
    flexShrink: 0, // Don't shrink
  },
  radioButtonContainerFraud: {
    display: "flex",
    width: "120px",
    padding: "12px",
    alignItems: "center",
    gap: "12px",
    borderRadius: "10px",
    border: "1px solid #DC2626",
    background: "linear-gradient(0deg, rgba(255, 255, 255, 0.90) 0%, rgba(255, 255, 255, 0.90) 100%), #DC2626",
    cursor: "pointer",
    justifyContent: "flex-start",
    flexShrink: 0, // Don't shrink
  },
  radioButtonContainerNotFraud: {
    display: "flex",
    width: "120px",
    padding: "12px",
    alignItems: "center",
    gap: "12px",
    borderRadius: "10px",
    border: "1px solid #16A34A",
    background: "linear-gradient(0deg, rgba(255, 255, 255, 0.90) 0%, rgba(255, 255, 255, 0.90) 100%), #16A34A",
    cursor: "pointer",
    justifyContent: "flex-start",
    flexShrink: 0, // Don't shrink
  },
  radioIndicator: {
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    border: "2px solid #D1D5DB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
  },
  radioIndicatorChecked: {
    borderColor: "#10B981",
    backgroundColor: "#10B981",
  },
  radioIndicatorFraud: {
    borderColor: "#DC2626",
    backgroundColor: "#DC2626",
  },
  radioIndicatorInner: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#FFFFFF",
  },
  radioText: {

    color: "#0A0A0A",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "100%",
    margin: 0,
  },
  separator: {
    width: "1px",
    height: "44px",
    backgroundColor: "#E5E5E5",
    flexShrink: 0, // Prevent shrinking
  },
  toggleSection: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0, // Prevent shrinking
    minWidth: "110px", // Ensure enough space for switch + text
  },
  toggleButton: {
    display: "flex",
    width: "36px",
    height: "20px",
    padding: "0 2px",
    alignItems: "center",
    borderRadius: "9999px",
    border: "2px solid transparent",
    backgroundColor: "#E5E5E5",
    position: "relative",
    cursor: "pointer",
  },
  toggleButtonChecked: {
    backgroundColor: "#3B82F6",
  },
  toggleThumb: {
    width: "14px",
    height: "14px",
    borderRadius: "50%",
    backgroundColor: "#FFFFFF",
    transition: "transform 0.2s ease",
    transform: "translateX(0px)",
  },
  toggleThumbChecked: {
    transform: "translateX(16px)",
  },
  toggleText: {
    color: "#0A0A0A",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "100%",
    whiteSpace: "nowrap", // Prevent text wrapping
  },
  toggleButtonDisabled: {
    cursor: "not-allowed",
  },
  toggleTextDisabled: {
    cursor: "not-allowed",
  },
  tooltipContent: {
    backgroundColor: "#1F2937",
    color: "#FFFFFF",
    fontSize: "12px",
    fontWeight: 500,
    fontFamily: "Inter, sans-serif",
    padding: "8px 12px",
    borderRadius: "6px",
    maxWidth: "200px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    userSelect: "none",
    animationDuration: "100ms",
    animationTimingFunction: "ease-out",
    willChange: "transform, opacity",
  },
}

export function FICPartyInvolvedCard({
  partyName,
  partyType,
  fraudStatus,
  generateRO = false,
  onFraudStatusChange,
  onGenerateROChange,
  generateRODisabled = false,
}: FICPartyInvolvedCardProps) {

  const getTooltipMessage = () => {
    if (generateRODisabled) {
      return "You cannot undo this action"
    }
    if (!generateRO) {
      return "Warning: You cannot undo this action once enabled"
    }
    return null
  }

  const tooltipMessage = getTooltipMessage()
  const showTooltip = tooltipMessage !== null

  return (
    <div style={styles.outerCard}>
      <div style={styles.partySection}>
        <h3 style={styles.partyName}>{partyName}</h3>
        <span style={styles.partyBadge}>{partyType}</span>
      </div>

      <div style={styles.controlsSection}>
        <RadioGroup.Root value={fraudStatus} onValueChange={onFraudStatusChange} style={styles.radioGroup}>
          <div style={fraudStatus === "fraud" ? styles.radioButtonContainerFraud : styles.radioButtonContainer}>
            <RadioGroup.Item
              value="fraud"
              style={{
                ...styles.radioIndicator,
                ...(fraudStatus === "fraud" ? styles.radioIndicatorFraud : {}),
              }}
            >
              <RadioGroup.Indicator style={styles.radioIndicatorInner} />
            </RadioGroup.Item>
            <label style={styles.radioText}>Fraud</label>
          </div>

          <div style={fraudStatus === "not-fraud" ? styles.radioButtonContainerNotFraud : styles.radioButtonContainer}>
            <RadioGroup.Item
              value="not-fraud"
              style={{
                ...styles.radioIndicator,
                ...(fraudStatus === "not-fraud" ? styles.radioIndicatorChecked : {}),
              }}
            >
              <RadioGroup.Indicator style={styles.radioIndicatorInner} />
            </RadioGroup.Item>
            <label style={styles.radioText}>Not Fraud</label>
          </div>
        </RadioGroup.Root>

        <div style={styles.separator} />

        <div style={styles.toggleSection}>
          <Tooltip.Provider>
            <Tooltip.Root open={showTooltip ? undefined : false} delayDuration={0}>
              <Tooltip.Trigger asChild>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Switch.Root
                    checked={generateRO}
                    onCheckedChange={generateRODisabled ? undefined : onGenerateROChange}
                    disabled={generateRODisabled}
                    style={{
                      ...styles.toggleButton,
                      ...(generateRO ? styles.toggleButtonChecked : {}),
                      ...(generateRODisabled ? styles.toggleButtonDisabled : {}),
                    }}
                  >
                    <Switch.Thumb
                      style={{
                        ...styles.toggleThumb,
                        ...(generateRO ? styles.toggleThumbChecked : {}),
                      }}
                    />
                  </Switch.Root>
                  <label style={{
                    ...styles.toggleText,
                    ...(generateRODisabled ? styles.toggleTextDisabled : {}),
                  }}>
                    Generate RO
                  </label>
                </div>
              </Tooltip.Trigger>
              {showTooltip && (
                <Tooltip.Portal>
                  <Tooltip.Content
                    style={styles.tooltipContent}
                    sideOffset={5}
                  >
                    {tooltipMessage}
                    <Tooltip.Arrow style={{ fill: "#1F2937" }} />
                  </Tooltip.Content>
                </Tooltip.Portal>
              )}
            </Tooltip.Root>
          </Tooltip.Provider>
        </div>
      </div>
    </div>
  )
}